import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../../components/ui/card';
import { DataTable, DataTableColumn } from '../../../components/ui/data-table';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Building2, Plus, Filter, Download, Edit, Trash2 } from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';
import TradingStatusBadge from '../../../components/trading/TradingStatusBadge';

type PAMMAccount = {
  id: string;
  accountNumber: number;
  accountType: 'master' | 'investor';
  strategyId?: string;
  strategyName?: string;
  balance: number;
  equity: number;
  margin: number;
  freeMargin: number;
  currency: string;
  leverage: string;
  status: 'active' | 'inactive' | 'suspended';
  createdAt: string;
};

const PAMMAccounts = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const accounts: PAMMAccount[] = [
    {
      id: 'ACC001',
      accountNumber: 1001234,
      accountType: 'master',
      strategyId: 'STR001',
      strategyName: 'Gold Scalper Pro',
      balance: 50000,
      equity: 52500,
      margin: 2500,
      freeMargin: 50000,
      currency: 'USD',
      leverage: '1:100',
      status: 'active',
      createdAt: '2024-01-15',
    },
    {
      id: 'ACC002',
      accountNumber: 1001235,
      accountType: 'investor',
      strategyId: 'STR001',
      strategyName: 'Gold Scalper Pro',
      balance: 25000,
      equity: 26250,
      margin: 1250,
      freeMargin: 25000,
      currency: 'USD',
      leverage: '1:100',
      status: 'active',
      createdAt: '2024-01-15',
    },
    {
      id: 'ACC003',
      accountNumber: 1001236,
      accountType: 'master',
      strategyId: 'STR002',
      strategyName: 'Forex Trend Master',
      balance: 75000,
      equity: 78000,
      margin: 3000,
      freeMargin: 75000,
      currency: 'USD',
      leverage: '1:200',
      status: 'active',
      createdAt: '2024-01-10',
    },
  ];

  const columns: DataTableColumn<PAMMAccount>[] = [
    {
      id: 'accountNumber',
      header: 'Account Number',
      accessor: row => <span className="font-medium">{row.accountNumber}</span>,
      sortable: true,
      sortValue: row => row.accountNumber,
      width: '150px',
    },
    {
      id: 'accountType',
      header: 'Type',
      accessor: row => (
        <Badge
          className={
            row.accountType === 'master'
              ? 'bg-purple-100 text-purple-800'
              : 'bg-blue-100 text-blue-800'
          }
        >
          {row.accountType.charAt(0).toUpperCase() + row.accountType.slice(1)}
        </Badge>
      ),
      sortable: true,
      sortValue: row => row.accountType,
      width: '120px',
    },
    {
      id: 'strategyName',
      header: 'Strategy',
      accessor: row => row.strategyName || '-',
      sortable: true,
      sortValue: row => row.strategyName || '',
      width: '180px',
    },
    {
      id: 'balance',
      header: 'Balance',
      accessor: row => `${row.currency} ${row.balance.toLocaleString()}`,
      sortable: true,
      sortValue: row => row.balance,
      width: '140px',
    },
    {
      id: 'equity',
      header: 'Equity',
      accessor: row => `${row.currency} ${row.equity.toLocaleString()}`,
      sortable: true,
      sortValue: row => row.equity,
      width: '140px',
    },
    {
      id: 'margin',
      header: 'Margin',
      accessor: row => `${row.currency} ${row.margin.toLocaleString()}`,
      sortable: true,
      sortValue: row => row.margin,
      width: '120px',
    },
    {
      id: 'leverage',
      header: 'Leverage',
      accessor: row => row.leverage,
      sortable: true,
      sortValue: row => row.leverage,
      width: '100px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => <TradingStatusBadge status={row.status} />,
      sortable: true,
      sortValue: row => row.status,
      width: '120px',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => console.log('Edit:', row.id)}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => console.log('Delete:', row.id)}
          >
            <Trash2 className="h-4 w-4 text-red-600" />
          </Button>
        </div>
      ),
      width: '120px',
    },
  ];

  const filteredAccounts = accounts.filter(account => {
    return (
      searchTerm === '' ||
      account.accountNumber.toString().includes(searchTerm) ||
      (account.strategyName &&
        account.strategyName
          .toLowerCase()
          .includes(searchTerm.toLowerCase())) ||
      account.id.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const totalBalance = accounts.reduce((sum, acc) => sum + acc.balance, 0);
  const masterAccounts = accounts.filter(
    acc => acc.accountType === 'master'
  ).length;
  const investorAccounts = accounts.filter(
    acc => acc.accountType === 'investor'
  ).length;

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Account Management"
          description="Manage PAMM master and investor accounts"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="flex items-center gap-2 p-4 bg-blue-50 rounded-lg">
          <Building2 className="h-5 w-5 text-blue-600" />
          <div>
            <p className="text-sm text-muted-foreground">Total Accounts</p>
            <p className="text-2xl font-bold text-blue-600">
              {accounts.length}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 p-4 bg-purple-50 rounded-lg">
          <Building2 className="h-5 w-5 text-purple-600" />
          <div>
            <p className="text-sm text-muted-foreground">Master Accounts</p>
            <p className="text-2xl font-bold text-purple-600">
              {masterAccounts}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 p-4 bg-green-50 rounded-lg">
          <Building2 className="h-5 w-5 text-green-600" />
          <div>
            <p className="text-sm text-muted-foreground">Investor Accounts</p>
            <p className="text-2xl font-bold text-green-600">
              {investorAccounts}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 p-4 bg-orange-50 rounded-lg">
          <Building2 className="h-5 w-5 text-orange-600" />
          <div>
            <p className="text-sm text-muted-foreground">Total Balance</p>
            <p className="text-2xl font-bold text-orange-600">
              ${totalBalance.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>All Accounts</CardTitle>
              <CardDescription>
                Manage and monitor all PAMM accounts
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </Button>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-2" />
                New Account
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={filteredAccounts}
            searchPlaceholder="Search by account number, strategy, or ID..."
            onSearchChange={setSearchTerm}
            showColumnVisibility={true}
            showExportButton={false}
            showAddButton={false}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default PAMMAccounts;
